import { useState } from 'react';
import { MIN_RADIUS_MILES, MAX_RADIUS_MILES, parseRadiusMiles } from '../../utils/radiusMiles';
import './RadiusInput.css';

interface RadiusInputProps {
  /** Current radius in whole miles. */
  value: number;
  /** Called with a valid whole number of miles (1–500) when the user commits a new value. */
  onChange: (miles: number) => void;
  id?: string;
  /** Extra class for page-specific sizing. */
  className?: string;
  ariaLabel?: string;
}

/**
 * SLV2-3509 (Clinic Network meeting 2026-09-30): free-typed radius in miles instead of a fixed dropdown.
 * The value is applied on Enter or when the box loses focus, so a search does not run on every keystroke.
 * An invalid entry shows a message and keeps the last valid radius.
 */
export default function RadiusInput({ value, onChange, id, className, ariaLabel = 'Radius in miles' }: Readonly<RadiusInputProps>) {
  const [text, setText] = useState(String(value));
  const [error, setError] = useState<string | null>(null);
  const [lastValue, setLastValue] = useState(value);

  // Follow a radius changed from outside (e.g. a page reset) — adjusted during render, React's
  // recommended alternative to syncing props into state with an effect.
  if (value !== lastValue) {
    setLastValue(value);
    setText(String(value));
    setError(null);
  }

  const commit = () => {
    const miles = parseRadiusMiles(text);
    if (miles == null) {
      setError(`Enter whole miles from ${MIN_RADIUS_MILES} to ${MAX_RADIUS_MILES}`);
      return;
    }
    setError(null);
    setText(String(miles));
    if (miles !== value) onChange(miles);
  };

  return (
    <span className={`radius-input ${className ?? ''}`.trim()}>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={`${MIN_RADIUS_MILES}–${MAX_RADIUS_MILES}`}
        value={text}
        onChange={(e) => { setText(e.target.value); if (error) setError(null); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setText(String(value)); setError(null); }
        }}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && id ? `${id}-error` : undefined}
      />
      {error && (
        <span className="radius-input-error" id={id ? `${id}-error` : undefined} role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
